<template>
  <context-bar-and-content class="pay-referral" v-loading="isLoadingEntity">
    <template slot="context-bar-left">
      <el-button icon="el-icon-arrow-left" @click="cancelPayout">Exit RAF Payout</el-button>
    </template>
    <template slot="context-bar-middle">
      {{ referrerName }}
    </template>
    <template slot="context-bar-right">
      &nbsp;
    </template>
    <div class="main-section">
      <el-row :gutter="18" class="mt-3">
        <el-alert class="mb-3" :title="errorMessage" type="error" show-icon v-if="errorMessage"></el-alert>
        <el-col :span="24" class="mb-3 client-header">
          <h2 class="align-center"><i :class="referrerIcon" /> {{ referrerName }} <span>Referrer</span></h2>
        </el-col>
        <el-col :lg="{ span: 8, offset: 8 }" :md="{ span: 12, offset: 6 }" :sm="{ span: 18, offset: 3 }"
          v-if="!transactionComplete">
          <el-form ref="payReferral" :model="form" :rules="rules" label-width="150px">
            <el-form-item label="Driver's License" prop="confirmDriversLicense">
              <el-input v-model="form.confirmDriversLicense" placeholder="Referring Client's Driver's License" />
            </el-form-item>
          </el-form>
        </el-col>
      </el-row>
      <el-row v-if="!transactionComplete">
        <el-col :span="18" :offset="3">
          <referral-table @selectionChange="onSelectionChanged" :for-cash-payout="true" :referrals="referrals" />
        </el-col>
        <el-col :span="22" :offset="1" class="align-center mt-3">
          <el-button type="primary" icon="el-icon-success" class="submit_btn" :loading="isLoading"
            :disabled="selectedReferrals.length == 0" @click="paySelectedReferrals">Pay Selected Referral(s)</el-button>
        </el-col>
      </el-row>
      <el-row v-if="transactionComplete">
        <h2 class="align-center">{{ referralsPaid.length }} referral(s) have been paid to the client.</h2>
        <h2 class="align-center">Total ${{ totalPaidAmount }}</h2>
        <p class="align-center">Transaction Id: {{ transactionId }}</p>
        <p class="align-center"><raf-payout-confirmation-pdf-button size="large" :transaction-id="transactionId"
            icon="el-icon-printer">Print Confirmation</raf-payout-confirmation-pdf-button></p>
      </el-row>
      <el-dialog title="Supervisor Approval" :visible.sync="supervisorSignInVisible" :show-close="false"
        :close-on-click-modal="false" :close-on-press-escape="false" width="30%">
        <supervisor-approval-dialog :callback="handleSupervisorApproval"
          @close="supervisorSignInVisible = false"></supervisor-approval-dialog>
      </el-dialog>
    </div>
  </context-bar-and-content>
</template>

<script>
import get from 'lodash/get';
import sumBy from 'lodash/sumBy';
import api from '@/api';
import ContextBarAndContent from '@/components/layout/ContextBarAndContent.vue';
import { Required, GenericDoubleBlindValidation } from '@/util/formValidators';
import ReferralTable from '@/components/tables/Referral.vue';
import ClientReferralPayoutTO from '@/model/ClientReferralPayoutTO';
import RafPayoutConfirmationPdfButton from '@/components/PDFButton/RAFPayoutConfirmation';
import SupervisorApprovalDialog from '@/components/dialog-content/SupervisorApproval.vue';
import { Client, Referral } from '@/model';

export default {
  name: 'pay-client-referral',
  data() {
    return {
      client: undefined,
      referrals: [],
      supervisorSignInVisible: false,
      isLoading: false,
      transactionComplete: false,
      transactionId: null,
      referralsPaid: [],
      errorMessage: null,
      form: {
        confirmDriversLicense: null,
      },
      rules: {
        confirmDriversLicense: [Required, { validator: this.mustMatch('referrer.stateId'), trigger: 'blur' }],
      },
      selectedReferrals: [],
      isLoadingEntity: false,
    };
  },
  watch: {
    '$route.params.entityId': {
      immediate: true,
      handler(id) {
        if (id === undefined) {
          return;
        }

        this.initEntityById(id);
      },
    },
  },
  methods: {
    async initEntityById(entityId) {
      this.referrals = [];
      this.client = undefined;

      this.isLoadingEntity = true;
      try {
        const clientResponse = await api.client.get(entityId);
        this.client = new Client(clientResponse.data);

        const referralsResponse = await api.client.getReferrals(entityId);
        this.referrals = referralsResponse.data.map(referral => new Referral(referral));
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'Error',
          message: `An error occurred. ${e.message}`,
        });
      } finally {
        this.isLoadingEntity = false;
      }
    },
    onSelectionChanged(selection) {
      this.selectedReferrals = selection;
    },
    cancelPayout() {
      this.$router.push({
        name: 'payReferral',
      });
    },
    async paySelectedReferrals() {
      this.errorMessage = null;
      try {
        await this.$refs.payReferral.validate();
      } catch (e) {
        console.warn('form not valid');
        return;
      }

      this.isLoading = true;
      const payoutTO = this.createTO();

      try {
        const response = await api.referral.payClientReferrals(payoutTO);
        this.transactionComplete = true;
        this.referralsPaid = response.data;
        this.transactionId = this.referralsPaid[0].transactionId;
      } catch (e) {
        if (get(e, 'response.status') === 403) {
          this.supervisorSignInVisible = true;
        } else {
          this.errorMessage = get(e, 'response.data', 'An error occurred.');
        }
      } finally {
        this.isLoading = false;
      }
    },
    async handleSupervisorApproval(token) {
      const payoutTO = this.createTO();
      this.isLoading = true;
      try {
        const response = await api.referral.payClientReferralsWithApproval(payoutTO, token);
        this.transactionComplete = true;
        this.referralsPaid = response.data;
        this.transactionId = this.referralsPaid[0].transactionId;
      } catch (e) {
        this.errorMessage = get(e, 'response.data', 'An error occurred.');
      } finally {
        this.isLoading = false;
      }
    },
    mustMatch(formItem) {
      return (...args) => {
        GenericDoubleBlindValidation(get(this, formItem), ...args);
      };
    },
    createTO() {
      const payoutTO = new ClientReferralPayoutTO();
      payoutTO.clientId = this.client.id;
      payoutTO.stateId = this.form.confirmDriversLicense;
      payoutTO.referrals = this.selectedReferrals.map(referral => referral.id);
      return payoutTO;
    },
  },
  computed: {
    referrer() {
      return this.client;
    },
    referrerIcon() {
      return 'fa fa-user-circle';
    },
    referrerName() {
      if (this.client !== undefined) {
        return `${this.client.lastName}, ${this.client.firstName}`;
      }
      return '';
    },
    totalPaidAmount() {
      return sumBy(this.referralsPaid, 'amount');
    },
  },
  components: {
    ContextBarAndContent,
    ReferralTable,
    RafPayoutConfirmationPdfButton,
    SupervisorApprovalDialog,
  },
};

</script>

<style lang="scss">
.pay-referral {
  .ssn-or-upload-wrapper {
    background: #F2F2F2;
    border-radius: 20px;

    >.el-col:not(:first-child) {
      border-left: 1px solid #CCC;
    }
  }

  .client-header {
    h2 {
      margin: 0;
      padding: 0 5px;
      font-size: 1.8em;

      span {
        font-weight: normal;
        color: #999;
        font-size: 0.75em;
        text-transform: uppercase;
      }
    }
  }

  .main-section {
    width: calc(100% - 18px);
  }
}
</style>
